import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { BrowserRouter } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { store, persistor } from "./store/store";
import { MsalInitializationService } from "./services/msalInitialization";
import {
  validateDomReady,
  validateBrowserSupport,
  setupGlobalErrorHandlers,
  setupPerformanceMonitoring,
} from "./utils/bootstrap";
import App from "./App";
import "./index.css";

// Validate environment variables first
import "./config/env";

// Bootstrap utilities
setupGlobalErrorHandlers();
setupPerformanceMonitoring();

// Enable detailed React error reporting in development
try {
  if (
    typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.DEV
  ) {
    console.log(
      "%c🚀 Running in Development Mode",
      "background: #0A96CC; color: white; padding: 4px 8px; border-radius: 4px;",
    );
    console.log(
      "%c⚠️  React warnings and errors will be displayed with detailed information",
      "color: #faad14;",
    );
  }
} catch (e) {
  // Silently ignore if import.meta is not available
}

// Validate environment before proceeding
validateBrowserSupport();
validateDomReady();

async function renderApp() {
  try {
    const msalInstance = await MsalInitializationService.getInstance();

    const root = ReactDOM.createRoot(document.getElementById("root")!);

    root.render(
      <React.StrictMode>
        <MsalProvider instance={msalInstance}>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <App />
              </BrowserRouter>
            </PersistGate>
          </Provider>
        </MsalProvider>
      </React.StrictMode>,
    );

    // Mark app as fully loaded for performance monitoring
    if ("performance" in window && "mark" in window.performance) {
      try {
        performance.mark("app-loaded");
        performance.measure("app-startup", "app-start", "app-loaded");
        const measure = performance.getEntriesByName("app-startup")[0];
        console.log(
          `[Performance] App startup time: ${measure.duration.toFixed(2)}ms`,
        );
      } catch (error) {
        console.warn("Performance measurement failed:", error);
      }
    }
  } catch (error) {
    console.error("Failed to render application:", error);

    // Fallback error display for critical initialization failures
    const root = document.getElementById("root");
    if (root) {
      root.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          height: 100vh;
          font-family: Arial, sans-serif;
          text-align: center;
          padding: 20px;
        ">
          <h1 style="color: #ef4444; margin-bottom: 16px;">Application Error</h1>
          <p style="color: #666; margin-bottom: 20px;">
            Failed to initialize the application. Please refresh the page or contact support.
          </p>
          <button
            onclick="window.location.reload()"
            style="
              padding: 10px 20px;
              background: #0A96CC;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
            "
          >
            Refresh Page
          </button>
        </div>
      `;
    }
  }
}

// Start the application
renderApp();
